import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import moment from "moment-timezone";
import { useModal } from "context/ModalContext";
import { submitUnlockPerformance, submitNotificationPerformance, setReopenModal } from "actions/App";
import { remoteUnlock, unauctionUnit, deleteLock, untransferUnit } from "actions/Units";
import { deleteUser, extendUserTransfer, removeManagerShare } from "actions/Users";
import useSingleCooldown from "hooks/useSingleCooldown";
import UnitTransferModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitTransferModal";
import UnitSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitSettings/UnitSettingsModal";
import UnitsUsersDetailModal from "app/routes/Sites/routes/Display/UnitsUsers/UnitsUsersDetailModal";
import LockSettingsModal from "app/routes/Sites/routes/Display/UnitsUsers/LockSettings/LockSettingsModal";
import AddUser from "components/AddUser";
import TableNew from "components/TableNew";
import Popout from 'components/Popout';
import PopoutDropdown from "components/PopoutDropdown";
import EmergencyUnlock from "components/EmergencyUnlock";
import AuctionUnit from "components/AuctionUnit";
import Tag from "components/Tag";
import ButtonContained from "components/ButtonContained";
import ButtonIconOnly from "components/ButtonIconOnly";

const UnitDetailModal = ({ uuDetail, unitUser, updateLocalShareInfo }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isCoolingDown, triggerCooldown } = useSingleCooldown();
  const { openModal, closeModal } = useModal();

  const authUser = useSelector(state => state.auth.authUser);
  const site = useSelector(state => state.auth.site);
  const [localShareWManagers, setLocalShareWManagers] = useState(unitUser?.shareWManagers);

  const getTag = (state) => {
    switch(state) {
      case "LOCKED":
        return <Tag text={intl.formatMessage({ id: "db.LOCKED" })} color="green" />
      case "UNLOCKED":
        return <Tag text={intl.formatMessage({ id: "db.UNLOCKED" })} color="red" />
      case "JAMMED_LOCKING":
      case "JAMMED_UNLOCKING":
      case "UGLY":
        return <Tag text={intl.formatMessage({ id: "db.jammed" })} color="orange" />
      default:
        return <Tag text={intl.formatMessage({ id: "form.na" })} color="gray" />
    }
  };

  const handleUnlockUnit = (lock) => {
    let inputHwType = "other";
    if (lock.hwType == "unit") {
      inputHwType = "unit";
    } else if (
      lock.hwType == "gate" ||
      lock.hwType == "exitgate" ||
      lock.hwType == "door" ||
      lock.hwType == "slidingdoor" ||
      lock.hwType == "elevator"
    ) {
      inputHwType = "gate";
    }
    let overwrite = {
      message: intl.formatMessage({ id: "notifications.success.unlock" }),
      errorMessage: intl.formatMessage({ id: "notifications.error.failed" }),
    };
    dispatch(
      submitUnlockPerformance(
        {
          site_id: site.id,
          user_id: authUser.id,
          unlock_type: "UnlockRemote",
          mac: lock.mac,
          overall_time: -1,
          start_time: moment().utc(),
          hwType: inputHwType,
          deviceType: lock.bleHwVersion,
          sourceSystem: "portal"
        },
        {
          facilityUUID: site.uuid,
          companyUUID: site.companyUUID
        }
      )
    );
    dispatch(
      submitNotificationPerformance(
        {
          site_id: site.id,
          user_id: authUser.id,
          notification_type: "unitNotifications",
          notification_name: "override_used",
          start_time: moment().utc()
        },
        {
          facilityUUID: site.uuid,
          companyUUID: site.companyUUID
        }
      )
    );
    dispatch(
      remoteUnlock({ mac: lock.mac }, overwrite)
    );
    triggerCooldown();
  };

  const handleUnassignAuction = () => {
    dispatch(
      unauctionUnit(
        {
          unitUUID: uuDetail.unit.uuid,
          userUUID: uuDetail.unit.User.uuid
        },
        {
          facilityUUID: site.uuid,
          companyUUID: site.companyUUID,
          message: intl.formatMessage({ id: "db.auction.unassign" }),
          refetch: "unituser"
        }
      )
    );
  };

  const handleAuctionUnit = (reassign = false) => {
    openModal(
      <AuctionUnit
        facility={site}
        selectedUnit={uuDetail.unit}
        mode={reassign ? "reassign" : "auction"}
        refetch={"unituser"}
      />,
      `${site.name} - ${intl.formatMessage({ id: "form.auctionMode" })}`
    );
  }

  const handleAssignUnit = () => {
    openModal(
      <AddUser
        facility={site}
        onClose={closeModal}
        type="tenant"
        preSelectedUnit={uuDetail.unit.uuid}
      />,
      `${site ? site.name : ""} - ${intl.formatMessage({ id: "form.assign.unit" })}`
    );
  }

  const handleTransferUnit = () => {
    openModal(
      <UnitTransferModal
        unit={uuDetail.unit}
      />,
      intl.formatMessage({ id: "comp.button.transfer.unit" })
    )
  }


  const handleOpenEmergencyUnlock = (unit, lock) => {
    openModal(
      <EmergencyUnlock
        lock={lock ? lock : unit.locks[0]}
        unit={unit}
        handleEmergencyUnlock={handleEmergencyUnlock}
      />,
      `Unlock ${lock ? lock.name : unit.locks[0]?.name}`
    );
  };

  const handleEmergencyUnlock = (lock, unit, type, selectedReason, reasonText, pinCode) => {
    let inputHwType = "other";
    if (lock.hwType == "unit") {
      inputHwType = "unit";
    } else if (
      lock.hwType == "gate" ||
      lock.hwType == "exitgate" ||
      lock.hwType == "door" ||
      lock.hwType == "slidingdoor" ||
      lock.hwType == "elevator"
    ) {
      inputHwType = "gate";
    }

    let overwrite = {
      facilityUUID: site.uuid,
      companyUUID: site.companyUUID,
      message: intl.formatMessage({ id: "notifications.success.unlock" }),
      errorMessage: intl.formatMessage({ id: "notifications.error.failed" }),
      refetch: "unituser"
    };

    if (type === "pin") {
      dispatch(
        remoteUnlock(
          {
            mac: lock.mac,
            overridePin: pinCode,
            overrideReason: "",
            unitUUID: unit.uuid
          },
          overwrite
        )
      );
    }
    if (type === "emergency") {
      dispatch(
        remoteUnlock(
          {
            mac: lock.mac,
            overrideReason: reasonText,
            unitUUID: unit.uuid
          },
          overwrite
        )
      );
    }

    dispatch(
      submitUnlockPerformance(
        {
          site_id: site.id,
          user_id: authUser.id,
          unlock_type: "UnlockRemote",
          mac: lock.mac,
          overall_time: -1,
          start_time: moment().utc(),
          hwType: inputHwType,
          deviceType: lock.bleHwVersion,
          sourceSystem: "portal"
        },
        {
          facilityUUID: site.uuid,
          companyUUID: site.companyUUID
        }
      )
    );

    dispatch(
      submitNotificationPerformance(
        {
          site_id: site.id,
          user_id: authUser.id,
          notification_type: "unitNotifications",
          notification_name: "override_used",
          start_time: moment().utc()
        },
        {
          facilityUUID: site.uuid,
          companyUUID: site.companyUUID
        }
      )
    );
    triggerCooldown();
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: false,
      render: (e) => {
        return (
          <div className="flex flex-col">
            <p className="tw-paragraph-reg font-semibold">{e.name}</p>
            {getTag(e.hwState)}
          </div>
        )
      }
    },
    {
      key: "mac",
      label: intl.formatMessage({ id: "comp.table.header.mac" }),
      sortable: false,
      width: "160px",
    },
    {
      key: "voltageBattery",
      label: intl.formatMessage({ id: "comp.table.header.batteryVoltage" }),
      sortable: false,
      width: "130px",
      render: (e) => {
        return (
          <p className="tw-paragraph-reg">{typeof e.voltageBattery === "number" ? (e.voltageBattery / 1000).toFixed(2) : "--"}</p>
        )
      }
    },
    {
      key: "voltageWired",
      label: intl.formatMessage({ id: "comp.table.header.wiredVoltage" }),
      sortable: false,
      width: "130px",
      render: (e) => {
        return (
          <p className="tw-paragraph-reg">{typeof e.voltageWired === "number" ? (e.voltageWired / 1000).toFixed(2) : "--"}</p>
        )
      }
    }
  ]

  if (authUser?.permissions?.includes("manage_locks")) {
    columns.push(
      {
        key: "settings",
        sortable: false,
        width: "90px",
        render: (e) => {
          return (
            <ButtonIconOnly
              id={`Unit-Detail-Modal-Lock-${e.id}-Settings-Icon`}
              dataCy={`Unit-Detail-Modal-Lock-${e.id}-Settings-Icon`}
              icon="settings"
              type="default"
              size={20}
              onClick={() => {
                openModal(
                  <LockSettingsModal lock={e} unitUUID={uuDetail.unit.uuid} />,
                  (
                    <LockSettingsModalHeader 
                      lock={e}
                      unitUser={unitUser}
                    />
                  )
                );
              }}
            />
          )
        }
      }
    )
  }

  const hdPadlockChecker = uuDetail.unit.locks?.length > 0 && 
    uuDetail.unit.locks.every(lock => lock?.bleHwVersion === '4I');

  return (
    <div className="flex flex-col justify-between min-h-[300px]">
      <div className="mb-8">
        {/* <div className="flex justify-end px-10">
          <ButtonIconOnly
            id="Unit-Detail-Modal-Settings-Icon"
            dataCy="Unit-Detail-Modal-Settings-Icon"
            icon="settings"
            type="default"
            onClick={() => {
              openModal(
                <UnitSettingsModal />,
                `${uuDetail.unit.name} - ${intl.formatMessage({ id: "pages.settings.Types" })}`
              );
            }}
          />
        </div> */}

        <TableNew 
          data={uuDetail.unit.locks}
          defaultSortKey="name"
          showHeaders={true}
          tableView={true}
          maxHeight="max-h-[200px]"
          columns={columns}
        />
      </div>

      <div className="flex gap-2 justify-start px-10">
        {/* Assign Unit Button */}
        {(uuDetail.unit.rentalState === "available" &&
          authUser?.permissions?.includes("manage_unit_assignments") && (
          site.pms === "" ||
          authUser.role.tier === 1 ||
          uuDetail.unit?.externalId?.startsWith("NOKE-")
        )) && <ButtonContained
          id="Unit-Detail-Modal-Assign-Unit-Button"
          dataCy="Unit-Detail-Modal-Assign-Unit-Button"
          text={intl.formatMessage({ id: "comp.button.assign.units" })}
          type="default"
          onClick={() => handleAssignUnit()}
        />}

        {/* Transfer Unit Button */}
        {(authUser?.permissions?.includes("manage_auction") && uuDetail.unit.rentalState === "inuse") && <ButtonContained
          id="Unit-Detail-Modal-Transfer-Unit-Button"
          dataCy="Unit-Detail-Modal-Transfer-Unit-Button"
          text={intl.formatMessage({ id: "comp.button.transfer.unit" }) }
          type="default"
          onClick={() => handleTransferUnit()}
        />}

        {/* Auction Unit Button */}
        {(uuDetail.unit.rentalState === "repo" || uuDetail.unit.rentalState === "overlock") && <ButtonContained
          id="Unit-Detail-Modal-Auction-Button"
          dataCy="Unit-Detail-Modal-Auction-Button"
          text={intl.formatMessage({ id: "form.auctionMode" })}
          type="default"
          onClick={() => handleAuctionUnit()}
        />}

        {/* End Auction Button */}
        {uuDetail.unit.rentalState === "auction-smartentry" && <ButtonContained
          id="Unit-Detail-Modal-Unassign-Auction-Button"
          dataCy="Unit-Detail-Modal-Unassign-Auction-Button"
          text={intl.formatMessage({ id: "form.unassignAuction" })}
          type="default"
          onClick={() => handleUnassignAuction()}
        />}

        {/* Unlock Unit Button */}
        {(uuDetail.unit.rentalState === "available" || uuDetail.unit.rentalState === "checkout") && 
         uuDetail.unit.locks?.length > 0 &&
         !hdPadlockChecker && (
          uuDetail.unit.locks.length > 1 ?
            <PopoutDropdown
              id="Unit-Detail-Modal-Unlock-Dropdown-Button"
              dataCy="Unit-Detail-Modal-Unlock-Dropdown-Button"
              label={intl.formatMessage({ id: "form.unlock" })}
              direction="up"
              align="left"
              largeButton={true}
              buttonIcon={null}
              options={
                uuDetail.unit.locks.map(l => ({
                  name: l.name,
                  value: l
                }))
              }
              onOptionSelect={(option) => handleUnlockUnit(option)}
            /> :
            <ButtonContained
              id="Unit-Detail-Modal-Unlock-Button"
              dataCy="Unit-Detail-Modal-Unlock-Button"
              text={intl.formatMessage({ id: "form.unlock" })}
              type="defaultOutline"
              disabled={uuDetail.unit.locks[0]?.gwDelinquent === 1 || isCoolingDown}
              onClick={() => handleUnlockUnit(uuDetail.unit.locks[0])}
            />
        )}

        {/* Reassign Auction Button */}
        {uuDetail.unit.rentalState === "auction-smartentry" && <ButtonContained
          id="Unit-Detail-Modal-Reassign-Auction-Button"
          dataCy="Unit-Detail-Modal-Reassign-Auction-Button"
          text={intl.formatMessage({ id: "form.reassignAuction" })}
          type="defaultOutline"
          onClick={() => handleAuctionUnit(true)}
        />}

        {/* Emergency Unlock Button */}
        {((uuDetail.unit.rentalState !== "available" && uuDetail.unit.rentalState !== "checkout") &&
          (!authUser?.permissions?.includes("installer_account")) &&
          ((authUser?.permissions?.includes("access_override_unlock") && (uuDetail.unit.rentalState !== "overlock" && uuDetail.unit.rentalState !== "repo" && uuDetail.unit.rentalState !== "auction-smartentry")) ||
          (authUser?.permissions?.includes("access_emergency_unlock"))) &&
          uuDetail.unit.locks?.length > 0 &&
          !hdPadlockChecker
        ) && (
          uuDetail.unit.locks?.length > 1 ?
            <PopoutDropdown
              id="Unit-Detail-Modal-Emergency-Unlock-Dropdown-Button"
              dataCy="Unit-Detail-Modal-Emergency-Unlock-Dropdown-Button"
              label={intl.formatMessage({ id: "form.emergencyUnlock" })}
              direction="up"
              align="left"
              largeButton={true}
              buttonIcon={null}
              options={
                uuDetail.unit.locks.map(l => ({
                  name: l.name,
                  value: l
                }))
              }
              onOptionSelect={(option) => handleOpenEmergencyUnlock(uuDetail.unit, option)}
            /> :
            <ButtonContained
              id="Unit-Detail-Modal-Emergency-Unlock-Button"
              dataCy="Unit-Detail-Modal-Emergency-Unlock-Button"
              text={intl.formatMessage({ id: "form.emergencyUnlock" })}
              type="defaultOutline"
              disabled={uuDetail.unit.locks?.[0]?.gwDelinquent === 1 || isCoolingDown}
              onClick={() => handleOpenEmergencyUnlock(uuDetail.unit)}
            />
        )}

        {/* Remove Manager Access Button */}
        {authUser?.permissions?.includes('open_manager_shares') &&
          localShareWManagers &&
          site?.allowManagerShares === "true" && <ButtonContained
          id="Unit-Detail-Modal-Remove-Manager-Button"
          dataCy="Unit-Detail-Modal-Remove-Manager-Button"
          text={intl.formatMessage({ id: "comp.button.removeManagerAccess" })}
          type="default"
          onClick={() => {
            dispatch(
              removeManagerShare(
                uuDetail.unit.id,
                {
                  failedMessage: intl.formatMessage({ id: "notifications.error.failed" }),
                  successMessage: intl.formatMessage({ id: "notifications.success.managerShare" })
                }
              )
            );
            // Update local state to hide button immediately
            setLocalShareWManagers(false);
            // Update UnitsUsers table to reflect shareWManagers change
            if (updateLocalShareInfo) {
              updateLocalShareInfo(unitUser.id.toString(), false);
            }
          }}
        />}

        {/* Extend Transfer Button */}
        {authUser?.permissions?.includes('manage_auction') && uuDetail.unit.rentalState === "transfer-smartentry" && <ButtonContained
          id="Unit-Detail-Modal-Extend-Transfer-Button"
          dataCy="Unit-Detail-Modal-Extend-Transfer-Button"
          text={intl.formatMessage({ id: "comp.button.transfer.extend" })}
          type="default"
          disabled={moment().diff(uuDetail.unit.transferDetails?.transferEnd, 'minutes', true) >= 1440} 
          onClick={() => dispatch(extendUserTransfer(uuDetail.unit.User.uuid, intl, { unitUUID: uuDetail.unit.uuid }))}
        />}

        {/* Unassign Transfer Button */}
        {authUser?.permissions?.includes('manage_auction') && uuDetail.unit.rentalState === "transfer-smartentry" && <ButtonContained
          id="Unit-Detail-Modal-Unassign-Transfer-Button"
          dataCy="Unit-Detail-Modal-Unassign-Transfer-Button"
          text={intl.formatMessage({ id: "form.unassignTransfer" })}
          type="default"
          onClick={() => dispatch(untransferUnit(
            {
              userUUID: uuDetail.unit.User.uuid,
              unitUUID: uuDetail.unit.uuid
            },
            {
              successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
            }
          ))}
        />}

        {/* View Unit Activity Button */}
        <ButtonContained
          id="Unit-Detail-Modal-View-Activity-Button"
          dataCy="Unit-Detail-Modal-View-Activity-Button"
          text={intl.formatMessage({ id: "comp.table.cell.viewActivity" })}
          type="defaultOutline"
          onClick={() => {
            closeModal();
            history.push(
              {
                pathname: "activity",
                state: {
                  predefinedFilters:
                    {
                      unitUUID: uuDetail.unit.uuid 
                    }
                }
              }
            );
          }}
        />

        {/* <ButtonContained
          id="Unit-Detail-Modal-Settings-Icon"
          dataCy="Unit-Detail-Modal-Settings-Icon"
          text={intl.formatMessage({ id: "comp.button.settings" })}
          type="defaultOutline"
          onClick={() => {
            openModal(
              <UnitSettingsModal />,
              `${uuDetail.unit.name} - ${intl.formatMessage({ id: "pages.settings.Types" })}`
            );
          }}
        /> */}
      </div>
    </div>
  )
}

// Separate header component with its own state management
const LockSettingsModalHeader = ({ lock, unitUser }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { openModal } = useModal();
  const popoutAnchorRef = useRef(null);

  const authUser = useSelector(state => state.auth.authUser);
  const [isPopoutOpen, setIsPopoutOpen] = useState(false);

  return (
    <div className="flex justify-between items-center mr-2">
      <Popout
        isOpen={isPopoutOpen}
        onClose={() => setIsPopoutOpen(false)}
        anchorRef={popoutAnchorRef}
        positioning="bottom-left"
        header={false}>
        <div className="max-w-[300px] gap-3 flex flex-col pt-6">
          <p className="tw-paragraph-lg">{intl.formatMessage({ id: "comp.form.delete.description" })}</p>
          <div className="flex gap-2 justify-start">
            <ButtonContained
              id="Lock-Settings-Modal-Confirm-Delete-Button"
              dataCy="Lock-Settings-Modal-Confirm-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.delete" })}
              type="default"
              onClick={() => {
                dispatch(
                  deleteLock(
                    { lockUUID: lock.uuid },
                    {
                      successMessage: intl.formatMessage({ id: "notifications.success.delete" }),
                      unitUUID: unitUser.uuid
                    }
                  )
                );
                setIsPopoutOpen(false);
              }}
            />
            <ButtonContained
              id="Lock-Settings-Modal-Cancel-Delete-Button"
              dataCy="Lock-Settings-Modal-Cancel-Delete-Button"
              text={intl.formatMessage({ id: "comp.button.cancel" })}
              type="defaultOutline"
              onClick={() => setIsPopoutOpen(false)}
            />
          </div>
        </div>
      </Popout>

      <div className="flex gap-2 items-center">
        <ButtonIconOnly
          id="Lock-Settings-Modal-Back-Button"
          dataCy="Lock-Settings-Modal-Back-Button"
          icon="arrowLeft"
          type="default"
          onClick={() => openModal(
            <UnitsUsersDetailModal
              unitUser={unitUser}
            />
          )}
        />
        <h4 className="tw-heading-4 text-[var(--color-gray-800)]">{`${lock.name} - ${intl.formatMessage({ id: "pages.settings.Types" })}`}</h4>
      </div>
      {unitUser.rentalState === "available" && authUser?.permissions?.includes('delete_locks') && <ButtonIconOnly
        ref={popoutAnchorRef}
        id="Lock-Settings-Modal-Delete-Lock-Button"
        dataCy="Lock-Settings-Modal-Delete-Lock-Button"
        icon="trash"
        type="alt"
        size={20}
        customColor="text-[var(--color-alert)]"
        onClick={() => setIsPopoutOpen(true)}
      />}
    </div>
  );
};

export default UnitDetailModal;